import { useAuth } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

const myRequestSchema = z.object({
  id: z.string(),
  referenceId: z.string(),
  subject: z.string(),
  category: z.string().nullable().optional(),
  status: z.enum(["PENDING", "IN_PROGRESS", "RESOLVED"]),
  type: z.enum(["SUPPORT", "DIRECT_MESSAGE"]).optional().default("SUPPORT"),
  senderDisplayName: z.string().nullable().optional(),
  senderAvatarUrl: z.string().nullable().optional(),
  userLastReadAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  _count: z.object({ replies: z.number() }).optional(),
});

const replySchema = z.object({
  id: z.string(),
  body: z.string(),
  imageUrl: z.string().nullable().optional(),
  sentByName: z.string().nullable().optional(),
  isInternal: z.boolean(),
  fromUser: z.boolean().optional(),
  createdAt: z.string(),
});

const myRequestDetailSchema = z.object({
  id: z.string(),
  referenceId: z.string(),
  email: z.string(),
  subject: z.string(),
  description: z.string(),
  category: z.string().nullable().optional(),
  status: z.enum(["PENDING", "IN_PROGRESS", "RESOLVED"]),
  priority: z.string(),
  phone: z.string().nullable().optional(),
  userLastReadAt: z.string().nullable().optional(),
  type: z.enum(["SUPPORT", "DIRECT_MESSAGE"]).optional().default("SUPPORT"),
  initiatedByAdmin: z.boolean().optional(),
  senderDisplayName: z.string().nullable().optional(),
  senderAvatarUrl: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  replies: z.array(replySchema),
});

export type MyRequest = z.infer<typeof myRequestSchema>;
export type MyRequestDetail = z.infer<typeof myRequestDetailSchema>;
export type MyReply = z.infer<typeof replySchema>;

export const useMyRequests = () => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["mySupport"],
    queryFn: async () => {
      const token = await getToken();
      const res = await axios.get(`${import.meta.env.VITE_API_URL}/app/support`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return z.array(myRequestSchema).parse(res.data);
    },
    refetchInterval: 30000,
  });
};

export const useMyRequestDetail = (id: string) => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["mySupport", id],
    queryFn: async () => {
      const token = await getToken();
      const res = await axios.get(`${import.meta.env.VITE_API_URL}/app/support/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return myRequestDetailSchema.parse(res.data);
    },
    refetchInterval: 5000,
  });
};

export const useUnreadMessageCount = () => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["unreadMessages"],
    queryFn: async () => {
      const token = await getToken();
      const res = await axios.get(`${import.meta.env.VITE_API_URL}/app/support/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return z.object({ count: z.number() }).parse(res.data).count;
    },
    refetchInterval: 30000,
  });
};

export const useMarkRead = () => {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (supportRequestId: string) => {
      const token = await getToken();
      await axios.post(
        `${import.meta.env.VITE_API_URL}/app/support/${supportRequestId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["unreadMessages"] });
      qc.invalidateQueries({ queryKey: ["mySupport"] });
    },
  });
};

export const useSendMyReply = () => {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ supportRequestId, body, image }: { supportRequestId: string; body: string; image?: File }) => {
      const token = await getToken();
      let data: FormData | { body: string };
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      if (image) {
        const formData = new FormData();
        formData.append('body', body);
        formData.append('image', image);
        data = formData;
      } else {
        data = { body };
      }
      const res = await axios.post(
        `${import.meta.env.VITE_API_URL}/app/support/${supportRequestId}/replies`,
        data,
        { headers },
      );
      return replySchema.parse(res.data);
    },
    onSuccess: (_reply, variables) => {
      qc.invalidateQueries({ queryKey: ["mySupport"] });
      qc.invalidateQueries({ queryKey: ["mySupport", variables.supportRequestId] });
    },
  });
};

export const useArchiveConversation = () => {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (supportRequestId: string) => {
      const token = await getToken();
      await axios.post(
        `${import.meta.env.VITE_API_URL}/app/support/${supportRequestId}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mySupport"] });
      qc.invalidateQueries({ queryKey: ["unreadMessages"] });
    },
  });
};
